import type { RoundEpisodeRow } from "../round-episodes";

// Which policies this episode should have produced a score row for.
//
// `is_filler` is a PER-SEAT flag, so most filler seats belong to real entrants:
// a policy seated on several agents has one credited seat and the rest tagged
// filler. A policy goes unscored only when EVERY one of its seats is filler —
// the same rule the backend tallies by (`tournament_sim/matrix.py`,
// `tally_episode`), which is why the test here is whether ANY non-filler seat
// holds it. That happens when a round is topped up with a policy no entrant
// fields: `record_episode_direct` (`queries/job_queries.py`) drops filler
// agents before aggregating per-policy rewards, so no EpisodePolicy row — and
// hence no `episode.scores` row — is ever written for it. Expecting one would
// judge EVERY episode in such a league incomplete and leave the showcase with
// nothing but a live match to fall back on.
function expectedScorePolicyIds(episode: RoundEpisodeRow): Set<string> {
  const entrantSeated = new Set(
    episode.participants.flatMap((participant) =>
      participant.kind === "policy" && !participant.is_filler
        ? [participant.policy_version_id]
        : [],
    ),
  );

  // No policy seat survived the filter — an all-filler roster, an all-human
  // one, or a row served without seat flags. Nothing here says which seats were
  // meant to be scored, so keep the old all-seats expectation rather than
  // expecting nothing and waving the episode through.
  if (entrantSeated.size === 0) return new Set(episode.policy_version_ids);

  return new Set(
    episode.policy_version_ids.filter((policyId) =>
      entrantSeated.has(policyId),
    ),
  );
}

function hasScoresForExpectedPolicies(episode: RoundEpisodeRow): boolean {
  const expectedPolicyIds = expectedScorePolicyIds(episode);

  if (expectedPolicyIds.size === 0) return true;

  const scoredPolicyIds = new Set(
    episode.scores.map((score) => score.policy_version_id),
  );
  return [...expectedPolicyIds].every((policyId) =>
    scoredPolicyIds.has(policyId),
  );
}

// The minimum the showcase frame needs to play an episode back: a completed
// run with a coworld session and a replay. Gates the round inspector's
// per-episode watch buttons — looser than isFullLengthCompletedReplay on
// purpose, because a viewer hand-picking an episode wants to see it even if a
// participant never landed in the recording or their score never landed.
export function isWatchableReplayEpisode(episode: RoundEpisodeRow): boolean {
  return (
    episode.status === "completed" &&
    Boolean(episode.coworld_id) &&
    Boolean(episode.episode_id) &&
    Boolean(episode.replay_url)
  );
}

export function isFullLengthCompletedReplay(episode: RoundEpisodeRow): boolean {
  if (!isWatchableReplayEpisode(episode)) {
    return false;
  }

  const expectedParticipants = episode.policy_version_ids.length;

  if (expectedParticipants === 0) return true;

  return (
    episode.participants.length >= expectedParticipants &&
    hasScoresForExpectedPolicies(episode)
  );
}

// A real, decided game: every expected policy has a score and somebody scored.
// A ZERO score is a legitimate outcome (an eliminated CTF team ends on 0 lives)
// — crashes don't reach here because isFullLengthCompletedReplay already
// requires episode status "completed". Only an all-zero game is unusable.
export function isScoredGame(episode: RoundEpisodeRow): boolean {
  const expectedPolicyIds = expectedScorePolicyIds(episode);

  if (expectedPolicyIds.size === 0) return false;

  return (
    hasScoresForExpectedPolicies(episode) && featuredMatchScore(episode) > 0
  );
}

// The peak score in a game — how decisive/watchable it was. Used to feature the
// best recent match rather than merely the newest.
export function featuredMatchScore(episode: RoundEpisodeRow): number {
  if (episode.scores.length === 0) return 0;

  return Math.max(...episode.scores.map((score) => score.score));
}
